import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Box,
  Chip,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Search, History } from '@mui/icons-material';
import {
  useOfficials,
  useOrganizations,
  usePlayers,
  useTeams,
  useTournaments,
  useVenues,
} from '@/hooks/useApi';
import { SkeletonList, ErrorState } from '@/components';

const TABS = [
  { id: 'tournaments', label: 'Турниры' },
  { id: 'teams', label: 'Команды' },
  { id: 'players', label: 'Игроки' },
  { id: 'venues', label: 'Площадки' },
  { id: 'organizations', label: 'Организации' },
  { id: 'officials', label: 'Судьи' },
] as const;
type SearchTab = (typeof TABS)[number]['id'];

const RECENT_KEY = 'search-recent';
const loadRecent = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
};
const saveRecent = (q: string) => {
  const next = [q, ...loadRecent().filter((x) => x !== q)].slice(0, 8);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
};

interface Row {
  id: string;
  title: string;
  subtitle?: string;
  href: string;
}

const useSearchResults = (tab: SearchTab, q: string) => {
  const params = { q, limit: 30 };
  const on = (t: SearchTab) => ({ queryKey: ['search', t, q], enabled: Boolean(q) && tab === t });
  const tournaments = useTournaments(params, on('tournaments'));
  const teams = useTeams(params, on('teams'));
  const players = usePlayers(params, on('players'));
  const venues = useVenues(params, on('venues'));
  const organizations = useOrganizations(params, on('organizations'));
  const officials = useOfficials(params, on('officials'));

  const map: Record<SearchTab, { rows: Row[]; query: { isLoading: boolean; error: unknown; refetch: () => unknown } }> = {
    tournaments: {
      query: tournaments,
      rows: (tournaments.data?.data ?? []).map((t) => ({ id: t.id, title: t.name, subtitle: t.season ?? undefined, href: `/tournaments/${t.id}` })),
    },
    teams: { query: teams, rows: (teams.data?.data ?? []).map((t) => ({ id: t.id, title: t.name, href: `/teams/${t.id}` })) },
    players: {
      query: players,
      rows: (players.data?.data ?? []).map((p) => ({ id: p.id, title: [p.firstName, p.lastName].filter(Boolean).join(' '), href: `/players/${p.id}` })),
    },
    venues: {
      query: venues,
      rows: (venues.data?.data ?? []).map((v) => ({ id: v.id, title: v.name, subtitle: v.address, href: `/venues?q=${encodeURIComponent(v.name)}` })),
    },
    organizations: {
      query: organizations,
      rows: (organizations.data?.data ?? []).map((o) => ({ id: o.id, title: o.name, subtitle: o.country ?? undefined, href: `/organizations?q=${encodeURIComponent(o.name)}` })),
    },
    officials: { query: officials, rows: (officials.data?.data ?? []).map((o) => ({ id: o.id, title: o.name, href: `/officials?q=${encodeURIComponent(o.name)}` })) },
  };
  return map[tab];
};

export const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (TABS.find((t) => t.id === searchParams.get('tab'))?.id ?? 'tournaments') as SearchTab;
  const q = searchParams.get('q') ?? '';
  const [input, setInput] = useState(q);
  const [recent, setRecent] = useState(loadRecent);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const value = input.trim();
      if (value === q) return;
      const next = new URLSearchParams(searchParams);
      if (value) next.set('q', value);
      else next.delete('q');
      setSearchParams(next, { replace: true });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [input, q, searchParams, setSearchParams]);

  const { rows, query } = useSearchResults(tab, q);

  const setTab = (value: SearchTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const open = (row: Row) => {
    saveRecent(q);
    setRecent(loadRecent());
    navigate(row.href);
  };

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1 }}>
      <TextField
        fullWidth
        autoFocus
        type="search"
        label="Поиск"
        placeholder="Название или имя"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        slotProps={{
          htmlInput: { enterKeyHint: 'search', autoComplete: 'off' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          },
        }}
      />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mt: 1 }}>
        {TABS.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} sx={{ minHeight: 48 }} />
        ))}
      </Tabs>

      {!q ? (
        recent.length > 0 ? (
          <Box sx={{ pt: 2 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              Недавние запросы
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {recent.map((r) => (
                <Chip key={r} icon={<History />} label={r} onClick={() => setInput(r)} sx={{ height: 40 }} />
              ))}
            </Stack>
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ pt: 3, textAlign: 'center' }}>
            Начните вводить запрос
          </Typography>
        )
      ) : query.isLoading ? (
        <SkeletonList count={5} />
      ) : query.error ? (
        <ErrorState message="Не удалось выполнить поиск" onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ pt: 3, textAlign: 'center' }}>
          Ничего не найдено
        </Typography>
      ) : (
        <List>
          {rows.map((row) => (
            <ListItemButton key={row.id} onClick={() => open(row)} sx={{ minHeight: 56, borderBottom: 1, borderColor: 'divider' }}>
              <ListItemText primary={row.title} secondary={row.subtitle} />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};
